// Qual tipo de link de e-mail abriu esta página — capturado SÍNCRONO, no import.
//
// ---------------------------------------------------------------------------
// O defeito que isto conserta
// ---------------------------------------------------------------------------
// `/redefinir-senha` decidia por `if (!sessao)`, e `sessao` é qualquer sessão do
// Supabase. Medido em produção em 18/08: um aluno apenas LOGADO que digita a URL
// recebe o formulário e define senha nova **sem saber a atual** — e o hub
// sincroniza a senha com o Memberkit, então leva as aulas junto. A premissa
// escrita no comentário da tela ("sem sessão, a pessoa chegou aqui direto") era
// "sessão ⇒ veio do e-mail", e ela é falsa.
//
// ---------------------------------------------------------------------------
// Por que NÃO serve olhar só o evento do onAuthStateChange
// ---------------------------------------------------------------------------
// Lido no auth-js 2.112.3 instalado (`GoTrueClient.js:410-417`), a emissão é
// EXCLUDENTE:
//
//     if (redirectType === 'recovery') PASSWORD_RECOVERY
//     else                             SIGNED_IN
//
// Ou seja: **convite emite SIGNED_IN**, indistinguível de um login com senha.
// Travar em PASSWORD_RECOVERY deixaria o aluno de primeiro acesso — exatamente
// quem a `liberar-primeiro-acesso` convida — sem conseguir definir senha. O
// convite é a porta do aluno novo; quebrá-la para fechar esta porta seria trocar
// um defeito por um pior.
//
// ---------------------------------------------------------------------------
// Por que SÍNCRONO, no import
// ---------------------------------------------------------------------------
// O cliente do Supabase apaga o hash (`window.location.hash = ''`,
// `GoTrueClient.js:3318`) no fim de `_getSessionFromURL`, depois de uma ida à
// rede. Ler no import acontece antes disso com folga e, mais importante, não
// depende de QUANDO o React monta — o que seria uma corrida sem árbitro.
//
// ---------------------------------------------------------------------------
// O que esta trava NÃO é
// ---------------------------------------------------------------------------
// Não é controle de segurança. Quem tem o navegador aberto e o DevTools chama
// `supabase.auth.updateUser({ password })` no console e passa por cima disto.
// O controle que não se contorna é do SERVIDOR — a reautenticação obrigatória
// para troca de senha, no painel do Supabase (Authentication → Providers →
// Email), que exige um código enviado por e-mail e ISENTA a sessão recente (a
// de recuperação é sempre recente). Medido em 27/08 pela Management API: ela
// está LIGADA (`security_update_password_require_reauthentication = true`), o
// SMTP é o SendGrid, e o e-mail do código tem template da casa
// (`supabase/emails/`, runbook `emails-do-auth.md`). O hub ainda não chama
// `reauthenticate()` em lugar nenhum — o código só sai se um dia chamar.
//
// O que esta trava resolve é a tela deixar de OFERECER o formulário a quem não
// chegou por link, que é como o defeito apareceu.

/**
 * Os tipos de link que autorizam definir senha.
 *
 * `recovery` (esqueci a senha) e `invite` (primeiro acesso) são os dois que o
 * projeto de fato gera — `resetPasswordForEmail` e `inviteUserByEmail`.
 *
 * `magiclink` e `signup` ficam FORA por decisão, não por esquecimento: nada no
 * hub os emite hoje (não há `signInWithOtp` nem cadastro público), e aceitar
 * tipo que ninguém gera é abrir caminho sem porta. Se um dia entrarem, entram
 * aqui de propósito.
 */
function linkAutoriza(tipo: string | null): boolean {
  return tipo === "recovery" || tipo === "invite";
}

function tipoNoHash(): string | null {
  if (typeof window === "undefined") return null;
  const hash = window.location.hash;
  if (hash.length < 2) return null;
  const p = new URLSearchParams(hash.slice(1));
  // `access_token` é o que caracteriza o callback implícito — é a mesma checagem
  // que o SDK faz em `_isImplicitGrantCallback`. Sem ele, um `type` solto na URL
  // não prova nada: aceitar `#type=recovery` sozinho seria destrancar a tela com
  // um texto que qualquer pessoa digita na barra de endereço.
  if (!p.get("access_token")) return null;
  return p.get("type");
}

/** O tipo lido da URL nesta carga da página. Nulo se não veio de link. */
export const tipoDoLink: string | null = tipoNoHash();

/** Esta carga da página nasceu de um link que autoriza definir senha? */
export const chegouPorLinkDeSenha: boolean = linkAutoriza(tipoDoLink);

const CHAVE = "cf.definir-senha";

// A permissão é guardada em memória E em sessionStorage. Em memória basta para o
// fluxo normal; o sessionStorage existe para o F5 no meio do caminho — o hash já
// foi apagado pelo SDK, e sem isto quem recarrega a página veria "peça outro
// link" tendo feito tudo certo. Morre com a aba, que é o alcance desejado.
let permitido: string | null = null;

/**
 * Registra que ESTA conta chegou por link.
 *
 * Guarda o id do usuário, e não um simples "sim": se o aluno terminar e outra
 * pessoa entrar na mesma aba, a permissão não vale para a conta nova.
 */
export function registrarPermissaoDeSenha(userId: string): void {
  permitido = userId;
  try {
    sessionStorage.setItem(CHAVE, userId);
  } catch {
    // Modo privado ou storage bloqueado: a permissão vale só nesta carga da
    // página. Silenciar aqui é certo — o fluxo funciona, só não sobrevive a F5.
  }
}

export function podeDefinirSenha(userId: string | undefined): boolean {
  if (!userId) return false;
  if (permitido === userId) return true;
  try {
    return sessionStorage.getItem(CHAVE) === userId;
  } catch {
    return false;
  }
}

/** Chamado depois de salvar a senha: a permissão era para UMA troca. */
export function esquecerPermissaoDeSenha(): void {
  permitido = null;
  try {
    sessionStorage.removeItem(CHAVE);
  } catch {
    // Nada a limpar se nunca deu para escrever.
  }
}
